import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  communityEngagementItems,
  learningHubPaths,
  projectSupportItems,
  siteMeta,
  storyHighlights,
} from "../content/site";
import { useDocumentMeta } from "../lib/meta";
import { ButtonLink } from "../components/ui/Button";

const heroStats = [
  { value: "501(c)(3)", label: "nonprofit stewardship" },
  { value: "3 modes", label: "formal, non-formal, informal" },
  { value: "Media + platforms", label: "mission delivery" },
] as const;

export const HomePage = () => {
  useDocumentMeta({
    title: "Lifelong-Learning Ecosystem Platform",
    description:
      "The Education Project is a 501(c)(3) nonprofit and lifelong-learning ecosystem platform advancing formal, non-formal, and informal learning.",
  });

  const [activePathIndex, setActivePathIndex] = useState(0);
  const [autoplayDelay, setAutoplayDelay] = useState(6000);

  useEffect(() => {
    const timer = setTimeout(() => {
      setActivePathIndex((prev) => (prev + 1) % learningHubPaths.length);
      setAutoplayDelay(6000);
    }, autoplayDelay);

    return () => clearTimeout(timer);
  }, [activePathIndex, autoplayDelay]);

  const handleManualChange = (newIndex: number) => {
    setActivePathIndex(newIndex);
    setAutoplayDelay(10000);
  };

  return (
    <div className="flex flex-col bg-white">
      <section
        className="relative overflow-hidden bg-cover bg-center bg-no-repeat min-h-[650px] md:min-h-[750px] pt-16 md:pt-24 pb-12 md:pb-16 flex flex-col justify-between"
        style={{ backgroundImage: "url('/hero-bg.png')" }}
      >
        {/* Dark Navy Overlay */}
        <div className="absolute inset-0 bg-[#0E1C2C]/90 md:bg-gradient-to-r md:from-[#0E1C2C] md:via-[#0E1C2C]/85 md:to-[#0E1C2C]/50" />
 
        <div className="relative w-full px-6 lg:px-12 flex flex-col justify-between flex-grow gap-16">
          {/* Top: Left-aligned Intro Text */}
          <div className="space-y-8 text-white max-w-6xl pt-8 md:pt-16">
            <p className="text-xs uppercase font-bold tracking-widest text-[#CEDC00]">
              A 501(c)(3) lifelong-learning ecosystem platform
            </p>
            <h1 className="text-5xl font-display font-bold leading-tight md:text-6xl lg:text-7xl text-white">
              Learning for every stage of life.
            </h1>
            <p className="max-w-4xl text-lg leading-relaxed text-slate-300 md:text-xl">
              The Education Project advances education through media, programs, digital platforms,
              partnerships, and community initiatives that support learning across formal, non-formal, and informal settings.
            </p>
          </div>
 
          {/* Bottom: Centered Stats and Actions */}
          <div className="flex flex-col items-center justify-center text-center space-y-6 w-full pb-0">
            <dl className="flex flex-col sm:flex-row items-center justify-center gap-8 md:gap-16 border-b border-white/20 pb-8 w-full max-w-4xl mx-auto">
              {heroStats.map((item, idx) => (
                <div key={item.label} className="flex items-center gap-8 md:gap-16">
                  <div className="space-y-1 text-center">
                    <dt className="text-xs uppercase tracking-wider text-slate-400">{item.label}</dt>
                    <dd className="text-xl font-display font-bold text-white md:text-2xl">{item.value}</dd>
                  </div>
                  {idx < heroStats.length - 1 && (
                    <div className="hidden sm:block h-10 w-px bg-white/20" />
                  )}
                </div>
              ))}
            </dl>

            <div className="flex flex-wrap items-center justify-center gap-6 w-full">
              <ButtonLink to="/contact" variant="lime">
                Partner With Us
              </ButtonLink>
              <ButtonLink to="/donate" variant="primary">
                Support the Mission &rarr;
              </ButtonLink>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section - Mission & Stewardship (Alternating Soft Blue Backdrop) */}
      <section className="bg-[#E2EEF3] border-b border-[#cce0ea] overflow-hidden border-t-4 border-t-[#CEDC00]">
        <div className="grid lg:grid-cols-2 items-stretch">
          <div className="relative min-h-[300px] lg:min-h-[450px] w-full">
            <img
              src="/classroom-people.png"
              alt="A diverse group of adult learners and an educator engaged in a classroom learning experience."
              className="absolute inset-0 h-full w-full object-cover"
            />
          </div>
          <div className="py-20 md:py-32 px-8 md:px-16 lg:px-24 flex items-center justify-center">
            <div className="grid gap-8 sm:grid-cols-2 max-w-2xl w-full">
              <article className="space-y-4 bg-white p-6 rounded-xl border border-stone-200/50 shadow-sm border-t-4 border-t-[#0293cc]">
                <p className="text-xs font-bold uppercase tracking-wider text-[#0293cc]">Mission positioning</p>
                <h2 className="text-2xl font-display font-bold text-[#0E1C2C]">Education in its broadest sense.</h2>
                <p className="text-sm text-stone-600 leading-relaxed">
                  Learning includes school-based study, community programs, professional growth, and
                  everyday discovery beyond the classroom.
                </p>
              </article>

              <article className="space-y-4 bg-white p-6 rounded-xl border border-stone-200/50 shadow-sm border-t-4 border-t-[#CEDC00]">
                <p className="text-xs font-bold uppercase tracking-wider text-[#CEDC00]">Trusted foundation</p>
                <h2 className="text-2xl font-display font-bold text-[#0E1C2C]">Nonprofit stewardship.</h2>
                <p className="text-sm text-stone-600 leading-relaxed">{siteMeta.trustStatement}</p>
              </article>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section - White Background */}
      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr] items-start pb-12">
            <div className="space-y-4">
              <p className="eyebrow text-[#0293cc]">Lifelong Learning</p>
              <h2 className="text-3xl font-display font-bold text-[#0E1C2C] md:text-5xl leading-tight">
                Education reaches beyond school.
              </h2>
            </div>
            <div>
              <p className="text-lg leading-relaxed text-stone-600 max-w-prose">
                The Education Project is dedicated to advancing formal, non-formal, and informal learning. 
                We empower individuals and organizations to share and access knowledge across life, creating 
                accessible pathways that support growth at every stage.
              </p>
            </div>
          </div>

          <div className="grid gap-8 md:grid-cols-3 border-t border-stone-200 pt-12">
            {[
              {
                num: "01",
                title: "Formal learning",
                body: "Supporting schools, educators, and structured settings with targeted resources, programs, and media.",
              },
              {
                num: "02",
                title: "Non-formal learning",
                body: "Designing workshops, community classes, and professional growth experiences outside the standard classroom.",
              },
              {
                num: "03",
                title: "Informal learning",
                body: "Enabling everyday discovery through public storytelling, digital archives, and shared community spaces.",
              },
            ].map((item) => (
              <article key={item.title} className="space-y-4 pt-2">
                <span className="block font-display text-4xl text-[#0293cc]/30 font-light">{item.num}</span>
                <h3 className="text-2xl font-display font-bold text-[#0E1C2C]">{item.title}</h3>
                <p className="text-stone-600 leading-relaxed text-sm md:text-base">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>


      {/* Featured Initiative - Deep Navy Panel with Lime Accent */}
      <section className="bg-[#0E1C2C] text-white overflow-hidden border-t-4 border-t-[#CEDC00]">
        <div className="grid lg:grid-cols-2 items-stretch">
          <div className="py-20 md:py-32 px-8 md:px-16 lg:px-24 flex items-center justify-center">
            <div className="max-w-xl w-full space-y-8">
              <div className="space-y-6">
                <p className="text-xs uppercase font-bold tracking-widest text-[#CEDC00]">Featured Initiative</p>
                <h2 className="text-4xl font-display font-bold text-white md:text-5xl leading-tight">
                  Education Beyond the Classroom
                </h2>
                <p className="text-lg leading-relaxed text-slate-300">
                  This flagship initiative demonstrates our ecosystem model in action: learning that moves fluidly 
                  through media, workshops, digital tools, and community-led partnerships rather than being confined to a single format.
                </p>
                <div className="pt-4">
                  <ButtonLink to="/programs#education-beyond-the-classroom" variant="lime" className="uppercase tracking-wider text-sm">
                    Explore Flagship Initiative
                  </ButtonLink>
                </div>
              </div>

              <div className="border-t border-white/10 pt-6 space-y-3">
                <p className="text-xs uppercase font-bold tracking-widest text-slate-400">Why this matters</p>
                <p className="text-slate-300 leading-relaxed text-sm md:text-base">
                  It provides a clear, practical example of how lifelong learning becomes accessible to the public, offering a template for future collaborative programs under nonprofit stewardship.
                </p>
              </div>
            </div>
          </div>
          <div className="relative min-h-[300px] lg:min-h-[450px] w-full">
            <img
              src="/workshop-discussion.png"
              alt="A diverse group of community members participating in an educational workshop discussion around a table."
              className="absolute inset-0 h-full w-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Community & Project Support - White Backdrop */}
      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="grid gap-16 lg:grid-cols-2">
            {/* Column 1: Partnerships */}
            <div className="space-y-8">
              <div className="space-y-4">
                <p className="eyebrow text-[#0293cc]">Community & Partnerships</p>
                <h2 className="text-3xl font-display font-bold text-[#0E1C2C]">Public Benefit Collaboration</h2>
                <p className="text-stone-600 leading-relaxed">
                  We work alongside schools, practitioners, donors, and civic institutions to build educational programs with wider reach and stronger relevance.
                </p>
              </div>

              <div className="space-y-6 border-t border-stone-200 pt-6">
                {communityEngagementItems.slice(0, 2).map((item) => (
                  <article key={item.title} className="space-y-2 bg-[#F5F9FB] p-5 rounded-xl border border-stone-200/50">
                    <h3 className="text-lg font-display font-bold text-[#0E1C2C]">{item.title}</h3>
                    <p className="text-sm text-stone-600 leading-relaxed">{item.body}</p>
                  </article>
                ))}
              </div>
            </div>

            {/* Column 2: Project Support */}
            <div className="space-y-8 lg:border-l lg:border-stone-200 lg:pl-12">
              <div className="space-y-4">
                <p className="eyebrow text-[#CEDC00]">Project Support</p>
                <h2 className="text-3xl font-display font-bold text-[#0E1C2C]">Fiscal Sponsorship</h2>
                <p className="text-stone-600 leading-relaxed">
                  We offer operational infrastructure, fiscal sponsorship, and network guidance to help promising educational projects grow responsibly.
                </p>
              </div>

              <div className="space-y-6 border-t border-stone-200 pt-6">
                {projectSupportItems.slice(0, 2).map((item) => (
                  <article key={item.title} className="space-y-2 bg-[#F5F9FB] p-5 rounded-xl border border-stone-200/50">
                    <h3 className="text-lg font-display font-bold text-[#0E1C2C]">{item.title}</h3>
                    <p className="text-sm text-stone-600 leading-relaxed">{item.body}</p>
                  </article>
                ))}
              </div>
              <div className="pt-2">
                <ButtonLink to="/project-support" variant="secondary" className="uppercase tracking-wider text-sm">
                  Learn About Project Support
                </ButtonLink>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Learning Hub Preview - Full-Section Carousel */}
      <section className="relative overflow-hidden bg-[#E2EEF3] border-y border-[#cce0ea] grid">
        <div className="grid grid-cols-1 grid-rows-1">
          {learningHubPaths.map((path, idx) => {
            const mainImage = path.images?.[0] || "/classroom-people.png";
            const isActive = idx === activePathIndex;

            return (
              <div
                key={path.slug}
                className={`col-start-1 row-start-1 grid lg:grid-cols-2 items-stretch min-h-[600px] lg:min-h-[700px] transition-opacity duration-700 ease-in-out ${
                  isActive ? "opacity-100 z-10 pointer-events-auto" : "opacity-0 z-0 pointer-events-none"
                }`}
              >
                {/* Left side: Text content and controls */}
                <div className="py-20 md:py-28 px-8 md:px-16 lg:px-24 flex flex-col justify-center space-y-8 bg-[#0E1C2C]">
                  <div className="space-y-4">
                    <h3 className="text-3xl md:text-5xl font-display font-bold text-white leading-tight">
                      {path.title}
                    </h3>
                    <p className="text-slate-300 text-base md:text-lg leading-relaxed max-w-prose">
                      {path.body}
                    </p>
                  </div>

                  {/* Inline slideshow controls */}
                  <div className="flex items-center gap-3 pt-6 border-t border-white/10">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleManualChange((activePathIndex - 1 + learningHubPaths.length) % learningHubPaths.length);
                      }}
                      className="p-2 rounded-full border border-white/20 hover:bg-white/10 text-white bg-white/5 transition-colors shadow-sm"
                      aria-label="Previous slide"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleManualChange((activePathIndex + 1) % learningHubPaths.length);
                      }}
                      className="p-2 rounded-full border border-white/20 hover:bg-white/10 text-white bg-white/5 transition-colors shadow-sm"
                      aria-label="Next slide"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    <div className="flex gap-1 ml-2">
                      {learningHubPaths.map((_, dotIdx) => (
                        <button
                          key={dotIdx}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleManualChange(dotIdx);
                          }}
                          className={`h-1.5 rounded-full transition-all duration-300 ${
                            dotIdx === activePathIndex ? "w-6 bg-[#CEDC00]" : "w-1.5 bg-white/20 hover:bg-white/40"
                          }`}
                          aria-label={`Go to slide ${dotIdx + 1}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right side: Single Photo */}
                <div className="relative h-[350px] md:h-[450px] lg:h-auto overflow-hidden bg-stone-100">
                  <img
                    src={mainImage}
                    alt={path.title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Stories / Insights Preview - Alternating Soft Blue Backdrop */}
      <section className="bg-[#E2EEF3] border-t border-[#cce0ea] py-16 md:py-24">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr] items-start pb-12">
            <div className="space-y-4">
              <p className="eyebrow text-[#0293cc]">Stories & Insights</p>
              <h2 className="text-3xl font-display font-bold text-[#0E1C2C] md:text-5xl leading-tight">
                Updates keeping the ecosystem connected.
              </h2>
            </div>
            <div className="space-y-6">
              <p className="text-stone-600 leading-relaxed max-w-prose">
                Our editorial highlights feature initiative milestones, reflections on learning format integration, and structural sponsorship insights.
              </p>
              <ButtonLink to="/stories" variant="secondary" className="uppercase tracking-wider text-sm">
                Read Stories & Insights
              </ButtonLink>
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-3 border-t border-stone-200 pt-12">
            {storyHighlights.map((story) => (
              <article
                key={story.slug}
                className="space-y-3 bg-white p-6 rounded-xl border border-stone-200/50 shadow-sm border-t-4 border-t-[#CEDC00] flex flex-col justify-between"
              >
                <div>
                  <p className="text-xs uppercase font-bold tracking-wider text-[#CEDC00] mb-2">Theme Insight</p>
                  <h3 className="text-xl font-display font-bold text-[#0E1C2C]">{story.title}</h3>
                  <p className="text-sm text-stone-600 leading-relaxed mt-2">{story.body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA Section - Full-bleed 50/50 Split */}
      <section className="bg-[#0E1C2C] overflow-hidden">
        <div className="grid lg:grid-cols-2 items-stretch">
          <div className="relative min-h-[400px] lg:min-h-[650px] w-full">
            <img
              src="/cta-home.png"
              alt="A diverse group of people of all ages learning together in a bright community library."
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0E1C2C]/40" />
          </div>
          <div className="py-20 md:py-32 px-8 md:px-16 lg:px-24 flex items-center justify-center">
            <div className="max-w-xl w-full space-y-6">
              <p className="text-xs uppercase font-bold tracking-widest text-[#CEDC00]">Support Lifelong Learning</p>
              <h2 className="text-3xl font-display font-bold text-white md:text-5xl leading-tight">
                Help make knowledge accessible across life.
              </h2>
              <p className="text-slate-300 leading-relaxed text-sm md:text-base">
                Your support helps sustain educational media, programs, partnerships, and the nonprofit infrastructure that keeps learning open to everyone.
              </p>
              <div className="flex flex-col gap-3 sm:flex-row pt-4">
                <ButtonLink to="/donate" variant="primary" className="uppercase tracking-wider text-sm !px-8">
                  Donate to the Mission
                </ButtonLink>
                <ButtonLink to="/contact" variant="lime" className="uppercase tracking-wider text-sm !px-8">
                  Start a Conversation
                </ButtonLink>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
